<template>
  <div class="documentos-emitidos">

    <!-- ── Encabezado ─────────────────────────────────────────────────────── -->
    <div class="encabezado-pagina">
      <div class="encabezado-contenido">
        <h1>
          <i class="bi bi-file-earmark-text"></i>
          Documentos Emitidos
        </h1>
        <p class="subtitulo">Gestiona las facturas electrónicas generadas y autorizadas</p>
      </div>
      <div class="encabezado-acciones">
        <button class="boton boton-secundario" @click="cargarFacturas" :disabled="cargando">
          <i class="bi bi-arrow-clockwise" :class="{ 'girando': cargando }"></i>
          Actualizar
        </button>
        <button
          class="boton boton-contorno-secundario"
          @click="mostrarFiltros = !mostrarFiltros"
          :class="{ 'activo': mostrarFiltros }"
        >
          <i class="bi" :class="mostrarFiltros ? 'bi-funnel-fill' : 'bi-funnel'"></i>
          Filtros
        </button>
        <router-link to="/ventas/facturacion" class="boton boton-primario">
          <i class="bi bi-plus-circle"></i>
          Nueva Factura
        </router-link>
      </div>
    </div>

    <!-- ── Filtros ────────────────────────────────────────────────────────── -->
    <div v-show="mostrarFiltros" class="tarjeta-filtros">
      <div class="fila-filtros">
        <div class="grupo-filtro">
          <label>Mes</label>
          <select v-model="filtros.mes" class="campo-entrada" @change="pagina = 0; cargarFacturas()">
            <option :value="null">Todos los meses</option>
            <option v-for="(nombre, idx) in MESES_NOMBRES" :key="idx + 1" :value="idx + 1">{{ nombre }}</option>
          </select>
        </div>
        <div class="grupo-filtro">
          <label>Año</label>
          <select v-model="filtros.anio" class="campo-entrada" @change="pagina = 0; cargarFacturas()">
            <option :value="null">Todos los años</option>
            <option v-for="a in aniosDisponibles" :key="a" :value="a">{{ a }}</option>
          </select>
        </div>
        <div class="grupo-filtro">
          <label>Estado</label>
          <select v-model="filtros.estado" class="campo-entrada" @change="cargarFacturas">
            <option value="">Todos</option>
            <option :value="ESTADO.BORRADOR">Borrador</option>
            <option :value="ESTADO.GENERADA">Generada</option>
            <option :value="ESTADO.FIRMADA">Firmada</option>
            <option :value="ESTADO.ENVIADA">Enviada</option>
            <option :value="ESTADO.AUTORIZADA">Autorizada</option>
            <option :value="ESTADO.RECHAZADA">Rechazada</option>
            <option :value="ESTADO.ERROR_TECNICO">Error técnico</option>
            <option :value="ESTADO.ANULADA">Anulada</option>
          </select>
        </div>
        <div class="grupo-filtro">
          <label>Tipo Documento</label>
          <select v-model="filtros.tipo_doc" class="campo-entrada" @change="pagina = 0; cargarFacturas()">
            <option value="">Todos</option>
            <option value="factura">Facturas</option>
            <option value="guia_remision">Guías de Remisión</option>
            <option value="nota_credito">Notas de Crédito</option>
            <option value="nota_venta">Notas de Venta</option>
          </select>
        </div>
        <div class="grupo-filtro grupo-filtro-cliente">
          <label>Cliente (RUC / Nombre)</label>
          <input
            v-model="filtros.cliente"
            type="text"
            class="campo-entrada"
            placeholder="Buscar por RUC, cédula o razón social..."
            @input="pagina = 0; cargarFacturas()"
          />
        </div>
        <div class="grupo-filtro">
          <label>Por página</label>
          <select :value="limite" class="campo-entrada" @change="cambiarLimite">
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div class="grupo-filtro grupo-filtro-acciones">
          <button class="boton boton-contorno-secundario boton-pequeno" @click="limpiarFiltros">
            <i class="bi bi-x-circle"></i> Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- ── Tabla ──────────────────────────────────────────────────────────── -->
    <div class="tarjeta-tabla">
      <!-- Cargando -->
      <div v-if="cargando" class="estado-carga">
        <div class="indicador-carga" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p>Cargando documentos...</p>
      </div>

      <!-- Sin resultados -->
      <div v-else-if="facturas.length === 0" class="estado-vacio">
        <i class="bi bi-inbox"></i>
        <h3>Sin documentos</h3>
        <p>No se encontraron documentos con los filtros aplicados</p>
      </div>

      <!-- Tabla de resultados -->
      <div v-else class="tabla-responsiva">
        <table class="tabla tabla-documentos">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Número</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th class="text-end">Subtotal</th>
              <th class="text-end">IVA</th>
              <th class="text-end">Total</th>
              <th>Estado</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="factura in facturas" :key="`${factura.tipo_doc || 'f'}-${factura.id}`">
              <td>
                <span :class="['etiqueta-tipo', claseTipo(factura.tipo_doc)]">
                  {{ etiquetaTipo(factura.tipo_doc) }}
                </span>
              </td>
              <td>
                <router-link
                  v-if="puedeEditarFactura(factura) && (!factura.tipo_doc || factura.tipo_doc === 'factura')"
                  :to="`/ventas/facturacion?edit=${factura.id}`"
                  class="numero-factura"
                  title="Editar factura"
                >
                  {{ factura.numero_factura || '-' }}
                </router-link>
                <router-link
                  v-else-if="!factura.tipo_doc || factura.tipo_doc === 'factura'"
                  :to="`/ventas/facturacion?view=${factura.id}`"
                  class="numero-factura"
                  title="Ver factura completa (solo lectura)"
                >
                  {{ factura.numero_factura || '-' }}
                </router-link>
                <a
                  v-else-if="factura.tipo_doc === 'nota_credito'"
                  href="#"
                  class="numero-factura"
                  title="Ver comprobante completo (solo lectura)"
                  @click.prevent="verComprobante(factura)"
                >
                  {{ factura.numero_factura || '-' }}
                  <span v-if="procesandoVista[factura.id]" class="spinner-border spinner-border-sm ms-1" style="width:.7rem;height:.7rem;"></span>
                </a>
                <strong v-else class="numero-factura">{{ factura.numero_factura || '-' }}</strong>
                <span v-if="factura.mensaje_sri" class="informacion-sri" tabindex="0" role="note" :title="factura.mensaje_sri" :aria-label="factura.mensaje_sri">
                  <i class="bi bi-info-circle" aria-hidden="true"></i>
                  <span class="detalle-informacion-sri" role="tooltip">{{ factura.mensaje_sri }}</span>
                </span>
              </td>
              <td>{{ formatearFecha(factura.fecha_emision) }}</td>
              <td>
                <div class="celda-cliente">
                  <span class="nombre-cliente">{{ factura.cliente_razon_social || factura.cliente_denominacion || '-' }}</span>
                  <small class="identificacion-cliente">{{ factura.cliente_identificacion }}</small>
                </div>
              </td>
              <td class="text-end">${{ formatearMonto(factura.subtotal) }}</td>
              <td class="text-end">${{ formatearMonto(factura.iva) }}</td>
              <td class="text-end"><strong>${{ formatearMonto(factura.total) }}</strong></td>
              <td>
                <span :class="['etiqueta-estado', claseEstado(factura.estado)]">
                  {{ factura.estado_texto || textoEstado(factura.estado) }}
                </span>
                <span v-if="factura.descuento_aprobacion === 'pendiente'" class="etiqueta-estado etiqueta-descuento-pendiente" title="Requiere aprobacion del administrador antes de firmar/enviar">
                  Descuento pendiente
                </span>
                <span
                  v-if="factura.mensaje_sri && [ESTADO.FIRMADA, ESTADO.ENVIADA].includes(Number(factura.estado))"
                  class="aviso-sri-pendiente"
                  :title="factura.mensaje_sri"
                >
                  <i class="bi bi-exclamation-triangle"></i>
                  Envío automático pendiente
                </span>
              </td>
              <td class="text-center">
                <div class="grupo-acciones">
                  <!-- Acciones exclusivas para facturas -->
                  <template v-if="!factura.tipo_doc || factura.tipo_doc === 'factura'">
                  <!-- Aprobar/rechazar descuento pendiente (solo administrador) -->
                  <template v-if="esAdministrador && factura.descuento_aprobacion === 'pendiente'">
                    <button
                      class="boton-accion boton-accion-exito"
                      title="Aprobar descuento"
                      :disabled="procesandoDescuento[factura.id]"
                      @click="aprobarDescuento(factura)"
                    >
                      <i class="bi bi-check-lg"></i>
                    </button>
                    <button
                      class="boton-accion boton-accion-peligro-contorno"
                      title="Rechazar descuento"
                      :disabled="procesandoDescuento[factura.id]"
                      @click="rechazarDescuento(factura)"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </template>

                  <!-- Firmar documento generado -->
                  <button
                    v-if="puedeFirmarDocumento(factura)"
                    class="boton-accion boton-accion-exito"
                    title="Firmar comprobante"
                    @click="abrirModalFirmar(factura)"
                  >
                    <i class="bi bi-pen"></i>
                  </button>

                  <!-- Enviar al SRI -->
                  <button
                    v-if="puedeEnviarSri(factura)"
                    class="boton-accion boton-accion-exito"
                    title="Enviar al SRI"
                    @click="abrirModalFirmar(factura)"
                  >
                    <i class="bi bi-cloud-upload"></i>
                  </button>

                  <!-- Eliminar: FIRMADA requiere comprobación previa contra el SRI -->
                  <button
                    v-if="puedeEliminar(factura)"
                    class="boton-accion boton-accion-peligro-contorno"
                    title="Eliminar factura"
                    @click="confirmarAccion('eliminar', factura)"
                  >
                    <i class="bi bi-trash3"></i>
                  </button>

                  <!-- Descargar XML (autorizada o anulada — se necesita para gestión en portal SRI) -->
                  <button
                    v-if="factura.estado == ESTADO.AUTORIZADA || factura.estado == ESTADO.ANULADA"
                    class="boton-accion boton-accion-info"
                    :title="factura.estado == ESTADO.ANULADA ? 'Descargar XML (necesario para anular en portal SRI)' : 'Descargar XML autorizado'"
                    @click="descargarXml(factura)"
                  >
                    <i class="bi bi-file-earmark-code"></i>
                  </button>

                  <!-- RIDE PDF A4 disponible desde la firma en modalidad offline -->
                  <button
                    v-if="puedeDescargarRide(factura)"
                    class="boton-accion boton-accion-peligro"
                    :title="factura.estado == ESTADO.ANULADA ? 'Descargar RIDE (constancia física — marcar como ANULADA)' : 'Descargar RIDE (PDF A4)'"
                    @click="descargarRide(factura)"
                  >
                    <i class="bi bi-file-pdf"></i>
                  </button>

                  <!-- Ticket térmico (autorizada o anulada) -->
                  <button
                    v-if="factura.estado == ESTADO.AUTORIZADA || factura.estado == ESTADO.ANULADA"
                    class="boton-accion boton-accion-neutro"
                    :title="factura.estado == ESTADO.ANULADA ? 'Imprimir ticket (constancia de anulación)' : 'Imprimir ticket térmico'"
                    @click="imprimirTicket(factura)"
                  >
                    <i class="bi bi-printer"></i>
                  </button>

                  <!-- Quitar firma / volver a XML generado -->
                  <button
                    v-if="puedeQuitarFirma(factura)"
                    class="boton-accion boton-accion-advertencia"
                    title="Anular firma"
                    @click="confirmarAccion('quitar-firma', factura)"
                  >
                    <i class="bi bi-arrow-counterclockwise"></i>
                  </button>

                  <!-- Consultar autorización (solo facturas ENVIADAS pendientes) -->
                  <button
                    v-if="Number(factura.estado) === ESTADO.ENVIADA"
                    class="boton-accion boton-accion-info"
                    :title="cooldownConsultaAuth[factura.id] ? 'El SRI sigue procesando — espere 1 minuto antes de reintentar' : 'Consultar autorización SRI'"
                    :disabled="procesandoConsultaAuth[factura.id] || cooldownConsultaAuth[factura.id]"
                    @click="consultarAutorizacion(factura)"
                  >
                    <i v-if="procesandoConsultaAuth[factura.id]" class="bi bi-hourglass-split"></i>
                    <i v-else-if="cooldownConsultaAuth[factura.id]" class="bi bi-clock-history"></i>
                    <i v-else class="bi bi-cloud-check"></i>
                  </button>

                  <!-- Reenviar al SRI (visible cuando 0 comprobantes o sin respuesta de autorización) -->
                  <button
                    v-if="Number(factura.estado) === ESTADO.ENVIADA && facturasSinComprobantes[factura.id]"
                    class="boton-accion boton-accion-advertencia"
                    title="Reenviar al SRI (el SRI no tiene respuesta de autorización para este comprobante)"
                    @click="reenviarConRegeneracion(factura)"
                  >
                    <i class="bi bi-cloud-arrow-up"></i>
                  </button>

                  <!-- Regenerar XML (RECHAZADA con fecha pasada o ERROR_TECNICO) -->
                  <button
                    v-if="puedeRegenerarXml(factura)"
                    class="boton-accion boton-accion-info"
                    title="Regenerar XML con fecha de hoy"
                    :disabled="procesandoRegenerar[factura.id]"
                    @click="regenerarXml(factura)"
                  >
                    <i v-if="procesandoRegenerar[factura.id]" class="bi bi-hourglass-split"></i>
                    <i v-else class="bi bi-arrow-repeat"></i>
                  </button>

                  <!-- Anular (SOLO facturas autorizadas por el SRI) -->
                  <button
                    v-if="puedeAnular(factura)"
                    class="boton-accion boton-accion-peligro-contorno"
                    title="Anular comprobante autorizado"
                    @click="confirmarAccion('anular', factura)"
                  >
                    <i class="bi bi-x-circle"></i>
                  </button>

                  <!-- Enviar por correo (XML autorizado + RIDE PDF adjuntos) -->
                  <button
                    v-if="Number(factura.estado) === ESTADO.AUTORIZADA"
                    class="boton-accion boton-accion-info"
                    title="Enviar comprobante por correo"
                    @click="abrirModalCorreo(factura)"
                  >
                    <i class="bi bi-envelope-arrow-up"></i>
                  </button>
                  </template>
                  <!-- Acciones para notas de crédito -->
                  <template v-else-if="factura.tipo_doc === 'nota_credito'">
                    <!-- Enviar al SRI (GENERADA, FIRMADA o RECHAZADA: firmar/reintentar) -->
                    <button
                      v-if="factura.estado == ESTADO.GENERADA || factura.estado == ESTADO.FIRMADA || factura.estado == ESTADO.RECHAZADA"
                      class="boton-accion boton-accion-advertencia"
                      :title="factura.estado == ESTADO.RECHAZADA ? 'Reintentar envío al SRI (rechazada)' : 'Firmar y enviar al SRI'"
                      :disabled="procesandoConsultaAuth[factura.id]"
                      @click="procesarNotaCreditoSri(factura)"
                    >
                      <i v-if="procesandoConsultaAuth[factura.id]" class="bi bi-hourglass-split"></i>
                      <i v-else class="bi bi-cloud-arrow-up"></i>
                    </button>
                    <!-- Consultar autorización (ENVIADA) -->
                    <button
                      v-if="factura.estado == ESTADO.ENVIADA"
                      class="boton-accion boton-accion-info"
                      :title="cooldownConsultaAuth[factura.id] ? 'El SRI sigue procesando — espere 1 minuto antes de reintentar' : 'Consultar autorización SRI'"
                      :disabled="procesandoConsultaAuth[factura.id] || cooldownConsultaAuth[factura.id]"
                      @click="procesarNotaCreditoSri(factura)"
                    >
                      <i v-if="procesandoConsultaAuth[factura.id]" class="bi bi-hourglass-split"></i>
                      <i v-else-if="cooldownConsultaAuth[factura.id]" class="bi bi-clock-history"></i>
                      <i v-else class="bi bi-cloud-check"></i>
                    </button>
                    <!-- Descargar XML (autorizada o anulada) -->
                    <button
                      v-if="factura.estado == ESTADO.AUTORIZADA || factura.estado == ESTADO.ANULADA"
                      class="boton-accion boton-accion-info"
                      :title="factura.estado == ESTADO.ANULADA ? 'Descargar XML (para anular en portal SRI)' : 'Descargar XML autorizado'"
                      @click="descargarXml(factura)"
                    >
                      <i class="bi bi-file-earmark-code"></i>
                    </button>
                    <!-- RIDE PDF disponible desde la firma en modalidad offline -->
                    <button
                      v-if="puedeDescargarRide(factura)"
                      class="boton-accion boton-accion-peligro"
                      :title="factura.estado == ESTADO.ANULADA ? 'Descargar RIDE (constancia de anulación)' : 'Descargar RIDE (PDF A4)'"
                      @click="descargarRide(factura)"
                    >
                      <i class="bi bi-file-pdf"></i>
                    </button>
                    <!-- Anular (solo autorizadas) -->
                    <button
                      v-if="factura.estado == ESTADO.AUTORIZADA"
                      class="boton-accion boton-accion-peligro-contorno"
                      title="Anular nota de crédito"
                      @click="confirmarAccion('anular', factura)"
                    >
                      <i class="bi bi-x-circle"></i>
                    </button>
                    <!-- Enviar por correo (solo autorizadas) -->
                    <button
                      v-if="factura.estado == ESTADO.AUTORIZADA"
                      class="boton-accion boton-accion-info"
                      title="Enviar comprobante por correo"
                      @click="abrirModalCorreo(factura)"
                    >
                      <i class="bi bi-envelope-arrow-up"></i>
                    </button>
                  </template>
                  <!-- Acciones para guías de remisión -->
                  <template v-else-if="factura.tipo_doc === 'guia_remision'">
                    <span class="texto-ayuda-accion" title="Guía de remisión">
                      <i class="bi bi-truck"></i>
                    </span>
                  </template>
                  <!-- Acciones para notas de venta (sin firma ni envio al SRI) -->
                  <template v-else-if="factura.tipo_doc === 'nota_venta'">
                    <button
                      class="boton-accion boton-accion-neutro"
                      title="Imprimir ticket"
                      @click="imprimirTicketNotaVenta(factura)"
                    >
                      <i class="bi bi-printer"></i>
                    </button>
                    <button
                      v-if="Number(factura.estado) !== ESTADO.ANULADA"
                      class="boton-accion boton-accion-peligro-contorno"
                      title="Anular nota de venta"
                      @click="confirmarAccion('anular', factura)"
                    >
                      <i class="bi bi-x-circle"></i>
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="facturas.length > 0" class="paginacion">
        <button
          class="boton boton-contorno-primario boton-pequeno"
          :disabled="pagina === 0"
          @click="cambiarPagina(-1)"
        >
          <i class="bi bi-chevron-left"></i> Anterior
        </button>
        <span class="info-pagina">
          Página {{ pagina + 1 }} &bull;
          <template v-if="totalDocumentos !== null">{{ totalDocumentos }} documentos en total</template>
          <template v-else>{{ facturas.length }} registros</template>
        </span>
        <button
          class="boton boton-contorno-primario boton-pequeno"
          :disabled="totalDocumentos !== null ? (pagina + 1) * limite >= totalDocumentos : facturas.length < limite"
          @click="cambiarPagina(1)"
        >
          Siguiente <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- ── Modal: contraseña del certificado ─────────────────────────────── -->
    <div v-if="modalFirmar.visible" class="capa-modal" @click.self="cerrarModalFirmar">
      <div class="caja-modal">
        <div class="modal-encabezado">
          <h5><i class="bi bi-shield-lock"></i> Firmar y Enviar al SRI</h5>
          <button class="boton-cerrar-modal" @click="cerrarModalFirmar" :disabled="modalFirmar.procesando">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="modal-cuerpo">
          <p>
            Se firmará y enviará la factura
            <strong>{{ modalFirmar.factura?.numero_factura }}</strong>
            al SRI para su autorización.
          </p>
          <div class="grupo-campo">
            <label class="etiqueta-campo">Contraseña del certificado digital (.p12)</label>
            <input
              v-model="modalFirmar.contrasena"
              type="password"
              class="campo-entrada"
              placeholder="Contraseña del certificado"
              @keyup.enter="ejecutarFirmarYEnviar"
            />
            <span class="texto-ayuda">Si tiene auto-firma habilitada, deje este campo vacío.</span>
          </div>
        </div>
        <div class="modal-pie">
          <button class="boton boton-secundario" @click="cerrarModalFirmar" :disabled="modalFirmar.procesando">
            Cancelar
          </button>
          <button class="boton boton-exito" @click="ejecutarFirmarYEnviar" :disabled="modalFirmar.procesando">
            <span v-if="modalFirmar.procesando">
              <span class="indicador-carga indicador-carga-pequeno me-1"></span>
              Procesando...
            </span>
            <span v-else>
              <i class="bi bi-send-check"></i> Firmar y Enviar
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal: resultado de autorización ──────────────────────────────── -->
    <div v-if="modalResultado.visible" class="capa-modal" @click.self="cerrarModalResultado">
      <div class="caja-modal caja-modal-amplia">
        <div
          class="modal-encabezado"
          :class="modalResultado.exitoso ? 'encabezado-exito' : 'encabezado-error'"
        >
          <h5>
            <i :class="modalResultado.exitoso ? 'bi bi-check-circle' : 'bi bi-x-circle'"></i>
            {{ modalResultado.exitoso ? 'Factura Autorizada' : 'Autorización Fallida' }}
          </h5>
          <button class="boton-cerrar-modal" @click="cerrarModalResultado">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="modal-cuerpo">
          <template v-if="modalResultado.exitoso">
            <div class="info-autorizacion">
              <div class="fila-dato">
                <span class="etiqueta-dato">Número de autorización</span>
                <span class="valor-dato valor-codigo">{{ modalResultado.datos.numero_autorizacion || '-' }}</span>
              </div>
              <div class="fila-dato">
                <span class="etiqueta-dato">Fecha de autorización</span>
                <span class="valor-dato">{{ formatearFechaHora(modalResultado.datos.fecha_autorizacion) || '-' }}</span>
              </div>
              <div class="fila-dato">
                <span class="etiqueta-dato">Clave de acceso</span>
                <span class="valor-dato valor-codigo valor-codigo-pequeno">{{ modalResultado.datos.clave_acceso || '-' }}</span>
              </div>
              <div class="fila-dato">
                <span class="etiqueta-dato">Ambiente</span>
                <span class="valor-dato">{{ modalResultado.datos.ambiente || '-' }}</span>
              </div>
            </div>
          </template>
          <template v-else>
            <p class="texto-error">{{ modalResultado.mensajeError }}</p>
          </template>
        </div>
        <div class="modal-pie">
          <button class="boton boton-secundario" @click="cerrarModalResultado">Cerrar</button>
          <button
            v-if="modalResultado.exitoso && modalResultado.datos.ride_url"
            class="boton boton-peligro"
            @click="abrirRideFactura(modalResultado.datos.factura_id)"
          >
            <i class="bi bi-file-pdf"></i> RIDE (PDF A4)
          </button>
          <button
            v-if="modalResultado.exitoso && modalResultado.datos.recibo_url"
            class="boton boton-contorno-secundario"
            @click="imprimirTicket({ id: modalResultado.datos.factura_id })"
          >
            <i class="bi bi-printer"></i> Ticket
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal: confirmación de acción destructiva ──────────────────────── -->
    <div v-if="modalConfirmacion.visible" class="capa-modal" @click.self="cancelarConfirmacion">
      <div class="caja-modal caja-modal-compacta">
        <div class="modal-encabezado encabezado-advertencia">
          <h5><i class="bi bi-exclamation-triangle"></i> Confirmación requerida</h5>
          <button class="boton-cerrar-modal" @click="cancelarConfirmacion">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="modal-cuerpo">
          <p>{{ modalConfirmacion.mensaje }}</p>
        </div>
        <div class="modal-pie">
          <button class="boton boton-secundario" @click="cancelarConfirmacion">Cancelar</button>
          <button class="boton boton-peligro" @click="ejecutarAccionConfirmada">
            <i class="bi bi-check-lg"></i> Confirmar
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal: Impresión de Ticket Térmico ──────────────────────────────── -->
    <div v-if="modalTicket.visible" class="capa-modal" @click.self="modalTicket.visible = false">
      <div class="caja-modal" style="max-width:440px; width:95%; padding:0; overflow:hidden;">
        <div class="modal-encabezado" style="padding: 10px 16px;">
          <h5><i class="bi bi-printer"></i> Ticket Térmico</h5>
          <button class="boton-cerrar-modal" @click="modalTicket.visible = false">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div style="background:#fff; overflow:hidden;">
          <iframe
            ref="iframeTicket"
            :src="modalTicket.html"
            style="width:100%; height:520px; border:none; display:block;"
          ></iframe>
        </div>
        <div class="modal-pie">
          <button class="boton boton-secundario" @click="modalTicket.visible = false">Cerrar</button>
          <button
            v-if="esAndroid"
            class="boton boton-secundario"
            style="background:#0dcaf0; color:#000; border-color:#0dcaf0;"
            @click="descargarTicketAndroid"
            title="Abre el ticket en el navegador para imprimir directamente"
          >
            <i class="bi bi-download"></i> Abrir en navegador
          </button>
          <button class="boton boton-primario" @click="imprimirDesdeModal">
            <i class="bi bi-printer"></i> Imprimir
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal: Enviar comprobante por correo ─────────────────────────── -->
    <div v-if="modalCorreo.visible" class="capa-modal" @click.self="cerrarModalCorreo">
      <div class="caja-modal caja-modal-compacta">
        <div class="modal-encabezado">
          <h5><i class="bi bi-envelope-arrow-up"></i> Enviar comprobante por correo</h5>
          <button class="boton-cerrar-modal" @click="cerrarModalCorreo" :disabled="modalCorreo.procesando">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="modal-cuerpo">
          <p>
            Se enviará el documento
            <strong>{{ modalCorreo.factura?.numero_factura }}</strong>
            con el <strong>XML autorizado por el SRI</strong> y el <strong>RIDE en PDF</strong> adjuntos.
          </p>
          <div class="grupo-campo">
            <label class="etiqueta-campo">Correo de destino</label>
            <input
              v-model="modalCorreo.correo"
              type="email"
              class="campo-entrada"
              placeholder="correo@ejemplo.com"
              :disabled="modalCorreo.procesando"
              @keyup.enter="ejecutarReenvioCorreo"
            />
            <span class="texto-ayuda">Deje vacío para usar el correo del cliente registrado en la factura.</span>
          </div>
        </div>
        <div class="modal-pie">
          <button class="boton boton-secundario" @click="cerrarModalCorreo" :disabled="modalCorreo.procesando">
            Cancelar
          </button>
          <button class="boton boton-primario" @click="ejecutarReenvioCorreo" :disabled="modalCorreo.procesando">
            <span v-if="modalCorreo.procesando">
              <span class="indicador-carga indicador-carga-pequeno me-1"></span>
              Enviando...
            </span>
            <span v-else>
              <i class="bi bi-send"></i> Enviar correo
            </span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api.js';
import notificaciones from '@/services/notificationService.js';
import controlAcceso from '@/services/controlAccesoService';
import { formatearFechaHoraSistema } from '@/utils/fechaSistema';

const ESTADO = {
  BORRADOR:      1,
  GENERADA:      2,
  FIRMADA:       3,
  ENVIADA:       4,
  AUTORIZADA:    5,
  RECHAZADA:     6,
  ERROR_TECNICO: 7,
  ANULADA:       8
};

export default {
  name: 'ClientDocumentosEmitidos',

  data() {
    return {
      ESTADO,
      MESES_NOMBRES: ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'],
      cargando:  false,
      facturas:  [],
      mostrarFiltros: false,
      filtros: {
        mes:  null,
        anio: null,
        estado:      '',
        tipo_doc:    '',
        cliente:     ''
      },
      limite: 50,
      pagina:  0,
      totalDocumentos: null,
      cargaId: 0,

      modalFirmar: {
        visible:           false,
        factura:           null,
        contrasena:        '',
        procesando:        false,
        forzarRegeneracion: false
      },

      modalResultado: {
        visible:      false,
        exitoso:      false,
        datos:        {},
        mensajeError: ''
      },

      modalConfirmacion: {
        visible:    false,
        mensaje:    '',
        tipoAccion: '',
        factura:    null
      },

      // Configuración de firma electrónica (para saber si hay contraseña guardada)
      firmaElectronica: null,

      // Control de estado de consulta de autorización por factura
      procesandoConsultaAuth: {},

      // Control de estado de regeneración de XML por factura
      procesandoRegenerar: {},

      // Cooldown por factura para evitar spam al SRI tras respuesta EN PROCESAMIENTO
      // { [facturaId]: true } — se limpia automáticamente con setTimeout
      cooldownConsultaAuth: {},

      // Facturas ENVIADAS donde el SRI devolvió 0 comprobantes (sin respuesta de autorización).
      // { [facturaId]: true } — indica que el usuario puede intentar reenviar.
      facturasSinComprobantes: {},

      // Modal de impresión de ticket térmico (reemplaza window.open)
      modalTicket: {
        visible: false,
        html: '',
        anchoMm: 80,
      },

      modalCorreo: {
        visible:    false,
        factura:    null,
        correo:     '',
        procesando: false
      },

      esAndroid: /Android/i.test(navigator.userAgent),
      esAdministrador: false,
      procesandoDescuento: {},

      // Control de estado al abrir el RIDE de solo lectura (numero de comprobante)
      procesandoVista: {},
    };
  },

  mounted() {
    this.cargarFacturas();
    this.cargarConfiguracionFirma();
    this.cargarEsAdministrador();
  },

  computed: {
    aniosDisponibles() {
      const anio = new Date().getFullYear()
      const lista = []
      for (let a = anio; a >= 2020; a--) lista.push(a)
      return lista
    },
  },

  methods: {
    // ── Carga de datos ───────────────────────────────────────────────────────

    async cargarFacturas() {
      const solicitudId = ++this.cargaId;
      this.cargando = true;
      try {
        const { mes, anio } = this.filtros;
        const parametros = {
          limit:  this.limite,
          offset: this.pagina * this.limite,
        };
        if (anio !== null) {
          const mesReal = mes !== null ? mes : 1;
          parametros.fecha_desde = `${anio}-${String(mesReal).padStart(2,'0')}-01`;
          if (mes !== null) {
            const ultimoDia = new Date(anio, mes, 0).getDate();
            parametros.fecha_hasta = `${anio}-${String(mes).padStart(2,'0')}-${String(ultimoDia).padStart(2,'0')}`;
          } else {
            parametros.fecha_hasta = `${anio}-12-31`;
          }
        } else if (mes !== null) {
          parametros.fecha_desde = `2020-${String(mes).padStart(2,'0')}-01`;
          const ultimoDia = new Date(new Date().getFullYear(), mes, 0).getDate();
          parametros.fecha_hasta = `${new Date().getFullYear()}-${String(mes).padStart(2,'0')}-${String(ultimoDia).padStart(2,'0')}`;
        }
        if (this.filtros.estado !== '') parametros.estado = this.filtros.estado;
        if (this.filtros.tipo_doc !== '') parametros.tipo_doc = this.filtros.tipo_doc;
        if (this.filtros.cliente !== '') parametros.cliente = this.filtros.cliente;

        const respuesta = await api.get('/documentos-emitidos', { params: parametros });
        if (solicitudId !== this.cargaId) return;
        this.facturas        = respuesta.data?.data ?? [];
        this.totalDocumentos = respuesta.data?.total ?? null;
      } catch (error) {
        if (solicitudId !== this.cargaId) return;
        console.error('[DocumentosEmitidos] Error al cargar documentos:', error);
        notificaciones.error('No se pudieron cargar los documentos. Intente nuevamente.');
        this.facturas = [];
        this.totalDocumentos = null;
      } finally {
        if (solicitudId === this.cargaId) this.cargando = false;
      }
    },

    limpiarFiltros() {
      this.filtros = {
        mes:  null,
        anio: null,
        estado:      '',
        tipo_doc:    '',
        cliente:     ''
      };
      this.pagina = 0;
      this.cargarFacturas();
    },

    cambiarPagina(delta) {
      this.pagina = Math.max(0, this.pagina + delta);
      this.cargarFacturas();
    },

    cambiarLimite(event) {
      this.limite = parseInt(event.target.value, 10) || 50;
      this.pagina = 0;
      this.cargarFacturas();
    },

    // ── Configuración ────────────────────────────────────────────────────────

    async cargarConfiguracionFirma() {
      try {
        const { data } = await api.get('/configuracion/firma-electronica');
        this.firmaElectronica = data?.data ?? null;
      } catch (e) {
        console.warn('[DocumentosEmitidos] No se pudo cargar configuración de firma:', e.message);
      }
    },

    // ── Modal firmar ─────────────────────────────────────────────────────────

    abrirModalFirmar(factura) {
      // FIRMADA: ya tiene firma, solo se envía — no se necesita contraseña
      if (Number(factura.estado) === ESTADO.FIRMADA) {
        this.modalFirmar = { visible: false, factura, contrasena: '', procesando: false, forzarRegeneracion: false };
        this.ejecutarFirmarYEnviar();
        return;
      }
      // Si hay contraseña guardada en el certificado, no pedir — firmar directo
      if (this.firmaElectronica?.tiene_contrasena_guardada) {
        this.modalFirmar = { visible: false, factura, contrasena: '', procesando: false, forzarRegeneracion: false };
        this.ejecutarFirmarYEnviar();
        return;
      }
      this.modalFirmar = {
        visible:           true,
        factura,
        contrasena:        '',
        procesando:        false,
        forzarRegeneracion: false
      };
    },

    // Reenviar al SRI sin regenerar (retoma el XML firmado existente)
    reenviarConRegeneracion(factura) {
      notificaciones.info('Reenviando comprobante al SRI...');
      if (this.firmaElectronica?.tiene_contrasena_guardada) {
        this.modalFirmar = { visible: false, factura, contrasena: '', procesando: false, forzarRegeneracion: false };
        this.ejecutarFirmarYEnviar();
        return;
      }
      this.modalFirmar = {
        visible:           true,
        factura,
        contrasena:        '',
        procesando:        false,
        forzarRegeneracion: false
      };
    },

    cerrarModalFirmar() {
      if (!this.modalFirmar.procesando) {
        this.modalFirmar.visible = false;
      }
    },

    async ejecutarFirmarYEnviar() {
      if (this.modalFirmar.procesando) return;
      this.modalFirmar.procesando = true;
      const facturaProcesada = this.modalFirmar.factura;
      const esSoloEnvio = [ESTADO.FIRMADA, ESTADO.ENVIADA].includes(Number(this.modalFirmar.factura?.estado));
      if (esSoloEnvio) {
        notificaciones.info('Reintentando envío al SRI...');
      } else {
        notificaciones.info('Firmando digitalmente...');
      }

      try {
        const carga = {};
        if (this.modalFirmar.contrasena) {
          carga.certificado_password = this.modalFirmar.contrasena;
        }

        notificaciones.info('Enviando al SRI...');
        const respuesta = await api.post(
          `/facturas/${this.modalFirmar.factura.id}/firmar-enviar`,
          carga
        );

        // Resetear procesando ANTES de cerrar para que cerrarModalFirmar() funcione
        this.modalFirmar.procesando = false;
        this.cerrarModalFirmar();

        // HTTP 202: el SRI recibió el comprobante pero aún no lo autoriza.
        // Se lanza polling automático cada 12 s hasta 5 intentos (~1 minuto).
        if (respuesta.status === 202 || respuesta.data?.en_proceso) {
          notificaciones.info('El SRI está procesando el comprobante. Consultando autorización automáticamente…');
          await this.cargarFacturas();
          await this.esperarAutorizacionAutomatica(facturaProcesada);
          return;
        }

        // Actualizar el estado de la factura localmente de inmediato
        const idx = this.facturas.findIndex(f => f.id === facturaProcesada.id);
        if (idx !== -1) {
          this.facturas[idx] = {
            ...this.facturas[idx],
            estado: 5,
            estado_texto: 'AUTORIZADA',
            numero_autorizacion: respuesta.data?.numero_autorizacion || null
          };
        }

        await this.cargarFacturas();
        const datosAutorizacion = this.mostrarModalAutorizacion(respuesta.data, facturaProcesada);
        notificaciones.success(
          `✅ Factura autorizada por el SRI exitosamente. Nº: ${datosAutorizacion.numero_autorizacion || 'N/A'}`
        );
      } catch (error) {
        const datos = error.response?.data;
        const mensajeError = datos?.message || datos?.error || '';

        // Sin clave guardada: mostrar modal para que el usuario la ingrese
        const codigo = datos?.codigo;
        if (error.response?.status === 400 && codigo === 'CERT_SIN_CLAVE') {
          this.modalFirmar.procesando = false;
          this.modalFirmar.visible    = true;
          this.modalFirmar.contrasena = '';
          return;
        }
        // Clave guardada pero no se pudo descifrar: error claro, sin modal
        if (error.response?.status === 400 && codigo === 'CERT_CLAVE_INVALIDA') {
          notificaciones.error('El certificado necesita ser re-subido. Vaya a Configuración → Firma Electrónica.');
          this.modalFirmar.procesando = false;
          return;
        }

        // 422: el SRI rechazó — mostrar el mensaje del SRI claramente
        if (error.response?.status === 422) {
          notificaciones.error(mensajeError || 'El SRI rechazó el comprobante.');
        } else {
          notificaciones.error(
            mensajeError || (esSoloEnvio
              ? 'Error al reenviar el comprobante al SRI.'
              : 'Error al firmar y enviar. Verifique la contraseña del certificado.')
          );
        }
        this.modalFirmar.procesando = false;
      } finally {
        // Garantizar que procesando nunca quede trabado
        if (this.modalFirmar.procesando) {
          this.modalFirmar.procesando = false;
        }
      }
    },

    cerrarModalResultado() {
      this.modalResultado.visible = false;
    },

    abrirEnlace(url) {
      window.open(url, '_blank');
    },

    async abrirRideFactura(facturaId) {
      if (!facturaId) {
        notificaciones.error('No se pudo identificar la factura para generar el RIDE.');
        return;
      }
      try {
        const respuesta = await api.get(`/facturas/${facturaId}/ride`, {
          responseType: 'blob'
        });

        const pdfBlob = respuesta.data instanceof Blob
          ? respuesta.data
          : new Blob([respuesta.data], { type: 'application/pdf' });

        if (!pdfBlob || pdfBlob.size === 0) {
          notificaciones.error('El servidor no devolvió un PDF válido para el RIDE.');
          return;
        }

        const url = URL.createObjectURL(pdfBlob);
        const win = window.open(url, '_blank');
        if (!win) {
          this.descargarArchivo(pdfBlob, 'application/pdf', `RIDE_${facturaId}.pdf`);
          notificaciones.warning('No se pudo abrir pestaña emergente. El RIDE se descargó automáticamente.');
        } else {
          notificaciones.success('RIDE PDF abierto en nueva pestaña.');
        }

        setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
      } catch (error) {
        const mensaje = await this.obtenerMensajeErrorApi(error, 'No se pudo generar el RIDE.');
        notificaciones.error(mensaje);
      }
    },

    // ── Descargas ────────────────────────────────────────────────────────────

    async descargarXml(factura) {
      try {
        const endpoint = factura.tipo_doc === 'nota_credito'
          ? `/notas-credito/${factura.id}/xml`
          : `/facturas/${factura.id}/xml`;
        const respuesta = await api.get(endpoint, { responseType: 'blob' });
        const nombre = factura.tipo_doc === 'nota_credito'
          ? `nc_${factura.clave_acceso || factura.id}.xml`
          : `factura_${factura.clave_acceso || factura.id}.xml`;
        this.descargarArchivo(respuesta.data, 'application/xml', nombre);
        notificaciones.success('XML descargado correctamente.');
      } catch (error) {
        const detalle = await this.obtenerMensajeErrorApi(error, 'No se pudo descargar el XML.');
        notificaciones.error(detalle);
      }
    },

    /**
     * Abre el RIDE (comprobante completo, PDF A4) en una pestaña nueva
     * en modo solo lectura — usado por el numero de comprobante cuando
     * el documento ya no es editable (firmado, enviado, autorizado,
     * rechazado, anulado, etc). No dispara descarga, solo visualizacion.
     */
    async verComprobante(factura) {
      if (this.procesandoVista[factura.id]) return;
      this.procesandoVista = { ...this.procesandoVista, [factura.id]: true };
      try {
        const endpoint = factura.tipo_doc === 'nota_credito'
          ? `/notas-credito/${factura.id}/ride`
          : `/facturas/${factura.id}/ride`;
        const respuesta = await api.get(endpoint, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([respuesta.data], { type: 'application/pdf' }));
        window.open(url, '_blank', 'noopener');
        // No se revoca el objectURL de inmediato: la pestaña nueva sigue leyendo el blob.
      } catch (error) {
        const detalle = await this.obtenerMensajeErrorApi(error, 'No se pudo abrir el comprobante.');
        notificaciones.error(detalle);
      } finally {
        this.procesandoVista = { ...this.procesandoVista, [factura.id]: false };
      }
    },

    async descargarRide(factura) {
      try {
        const endpoint = factura.tipo_doc === 'nota_credito'
          ? `/notas-credito/${factura.id}/ride`
          : `/facturas/${factura.id}/ride`;
        const respuesta = await api.get(endpoint, { responseType: 'blob' });

        const nombrePorDefecto = `RIDE_${factura.numero_factura || factura.id}.pdf`;
        const nombreArchivo = this.extraerNombreArchivoDesdeDisposition(
          respuesta.headers?.['content-disposition'],
          nombrePorDefecto
        );

        this.descargarArchivo(respuesta.data, 'application/pdf', nombreArchivo);
        notificaciones.success('RIDE PDF descargado correctamente.');
      } catch (error) {
        const detalle = await this.obtenerMensajeErrorApi(error, 'No se pudo generar el RIDE.');
        notificaciones.error(detalle);
      }
    },

    async imprimirTicket(factura) {
      try {
        const respuesta = await api.get(`/facturas/${factura.id}/ticket`);
        const html = respuesta.data?.html;
        if (html) {
          // Leer ancho de papel desde el meta tag generado por el backend
          const metaAncho = html.match(/<meta\s+name="paper-width"\s+content="(\d+)mm"/i);
          const anchoMm   = metaAncho ? parseInt(metaAncho[1], 10) : 80;
          if (this.esAndroid) {
            this._abrirTicketAndroid(html);
          } else {
            const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
            this.modalTicket.html    = blobUrl;
            this.modalTicket.anchoMm = anchoMm;
            this.modalTicket.visible = true;
          }
        }
      } catch (error) {
        notificaciones.error('No se pudo generar el ticket: ' + (error.response?.data?.error || error.message));
      }
    },

    async imprimirTicketNotaVenta(factura) {
      try {
        const respuesta = await api.get(`/notas-venta/${factura.id}/ticket`);
        const html = respuesta.data?.html;
        if (html) {
          if (this.esAndroid) {
            this._abrirTicketAndroid(html);
          } else {
            const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
            this.modalTicket.html    = blobUrl;
            this.modalTicket.anchoMm = 80;
            this.modalTicket.visible = true;
          }
        }
      } catch (error) {
        notificaciones.error('No se pudo generar el ticket: ' + (error.response?.data?.error || error.message));
      }
    },

    /**
     * Android: inyecta script de auto-impresión y abre en nueva ventana.
     * Chrome Android en ventana propia puede usar el servicio de impresión
     * del sistema operativo (Sunmi InnerPrinter, iMin, etc.).
     */
    _abrirTicketAndroid(html) {
      const scriptAutoPrint = `<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},600);});</` + `script>`;
      const htmlFinal = html.includes('</body>')
        ? html.replace('</body>', scriptAutoPrint + '</body>')
        : html + scriptAutoPrint;
      const blob = new Blob([htmlFinal], { type: 'text/html' });
      const url  = URL.createObjectURL(blob);
      const win  = window.open(url, '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        this.modalTicket.html    = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        this.modalTicket.visible = true;
      }
    },

    imprimirDesdeModal() {
      // En Android, si el modal está visible (popup bloqueado), abrir en ventana nueva
      if (this.esAndroid && this.modalTicket.html) {
        const win = window.open(this.modalTicket.html, '_blank');
        if (win) { this.modalTicket.visible = false; return; }
      }

      const iframe  = this.$refs.iframeTicket;
      const anchoMm = this.modalTicket.anchoMm || 80;
      if (!iframe || !iframe.contentWindow) return;

      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        if (doc) {
          let estilo = doc.getElementById('__page_override');
          if (!estilo) {
            estilo = doc.createElement('style');
            estilo.id = '__page_override';
            doc.head.appendChild(estilo);
          }
          estilo.textContent = `
            @page { size: ${anchoMm}mm auto !important; margin: 2mm 1mm !important; }
            @media print { html, body { width: ${anchoMm}mm !important; margin: 0 !important; } }`;
        }
      } catch (e) { /* iframe origen cruzado */ }

      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    },

    descargarTicketAndroid() {
      const url = this.modalTicket.html;
      if (!url) return;
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ticket.html';
      a.click();
    },

    /**
     * Detecta si es Android para decidir entre modal e iframe.
     */
    detectarAnchoSunmi() {
      return null; // Sunmi eliminado
    },

    extraerNombreArchivoDesdeDisposition(contentDisposition, fallback = 'archivo.pdf') {
      if (!contentDisposition || typeof contentDisposition !== 'string') {
        return fallback;
      }

      // Soporta: filename="x.pdf" y filename*=UTF-8''x.pdf
      const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
      if (utf8Match?.[1]) {
        try {
          return decodeURIComponent(utf8Match[1].replace(/["']/g, '')) || fallback;
        } catch (e) {
          return fallback;
        }
      }

      const fileMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      if (fileMatch?.[1]) {
        return fileMatch[1].trim() || fallback;
      }

      return fallback;
    },

    construirDatosAutorizacion(payload = {}, factura = null) {
      const fuente = payload?.data && typeof payload.data === 'object'
        ? { ...payload, ...payload.data }
        : (payload || {});

      const facturaId = fuente.factura_id || factura?.id || null;
      const facturaActualizada = facturaId
        ? this.facturas.find((f) => Number(f.id) === Number(facturaId))
        : null;

      const numeroAutorizacion =
        fuente.numero_autorizacion ||
        fuente.numeroAutorizacion ||
        facturaActualizada?.numero_autorizacion ||
        factura?.numero_autorizacion ||
        null;

      const fechaAutorizacion =
        fuente.fecha_autorizacion ||
        fuente.fechaAutorizacion ||
        facturaActualizada?.fecha_autorizacion ||
        factura?.fecha_autorizacion ||
        null;

      const claveAcceso =
        fuente.clave_acceso ||
        fuente.claveAcceso ||
        facturaActualizada?.clave_acceso ||
        factura?.clave_acceso ||
        null;

      const ambiente =
        fuente.ambiente ||
        fuente.ambiente_nombre ||
        (facturaActualizada?.ambiente_nombre ? String(facturaActualizada.ambiente_nombre).toUpperCase() : null) ||
        (factura?.ambiente_nombre ? String(factura.ambiente_nombre).toUpperCase() : null) ||
        null;

      return {
        ...fuente,
        factura_id: facturaId,
        numero_autorizacion: numeroAutorizacion,
        fecha_autorizacion: fechaAutorizacion,
        clave_acceso: claveAcceso,
        ambiente
      };
    },

    mostrarModalAutorizacion(payload = {}, factura = null) {
      const datos = this.construirDatosAutorizacion(payload, factura);
      this.modalResultado = {
        visible: true,
        exitoso: true,
        datos,
        mensajeError: ''
      };
      return datos;
    },

    async obtenerMensajeErrorApi(error, fallback = 'Error inesperado.') {
      const data = error?.response?.data;

      if (data instanceof Blob) {
        try {
          const texto = await data.text();
          const json = JSON.parse(texto);
          const mensaje = json?.error || json?.message || '';
          if (mensaje) return mensaje;
          return texto || fallback;
        } catch (e) {
          // Si no es JSON, intentar mostrar texto plano
          try {
            const textoPlano = await data.text();
            if (textoPlano) return textoPlano;
          } catch (e2) {
            // Ignorar y usar fallback
          }
        }
      }

      return data?.error || data?.message || error?.message || fallback;
    },

    descargarArchivo(datos, tipo, nombreArchivo) {
      const url  = URL.createObjectURL(new Blob([datos], { type: tipo }));
      const link = document.createElement('a');
      link.href     = url;
      link.download = nombreArchivo;
      link.click();
      URL.revokeObjectURL(url);
    },

    // ── Confirmación y acciones destructivas ─────────────────────────────────

    confirmarAccion(tipo, factura) {
      const mensajes = {
        'quitar-firma': `¿Quitar la firma de la factura ${factura.numero_factura}? Primero se verificará que el SRI no haya recibido la clave; el documento volverá a estado GENERADA.`,
        'anular':       factura.tipo_doc === 'nota_venta'
          ? `¿Anular la nota de venta ${factura.numero_factura}? Se devolverá el inventario descontado y se revertirá el cobro registrado. Esta acción no se puede deshacer.`
          : Number(factura.estado) === ESTADO.AUTORIZADA
            ? `¿Marcar como ANULADA la factura ${factura.numero_factura}? El sistema la registrará como anulada, pero deberá completar la anulación legal en el portal del SRI (sri.gob.ec) dentro del mes de emisión.`
            : `¿Anular la factura ${factura.numero_factura}? Esta acción no se puede deshacer.`,
        'eliminar':     `¿Eliminar definitivamente la factura ${factura.numero_factura}? Si está firmada, primero se comprobará que el SRI no la recibió. Se devolverá el inventario y el secuencial solo retrocederá si es el último.`
      };
      this.modalConfirmacion = {
        visible:    true,
        mensaje:    mensajes[tipo] || '¿Confirma esta acción?',
        tipoAccion: tipo,
        factura
      };
    },

    cancelarConfirmacion() {
      this.modalConfirmacion.visible = false;
    },

    async ejecutarAccionConfirmada() {
      const { tipoAccion, factura } = this.modalConfirmacion;
      this.modalConfirmacion.visible = false;

      if (tipoAccion === 'quitar-firma') {
        await this.quitarFirma(factura);
      } else if (tipoAccion === 'anular') {
        await this.anularFactura(factura);
      } else if (tipoAccion === 'eliminar') {
        await this.eliminarFactura(factura);
      }
    },

    async quitarFirma(factura) {
      try {
        await api.post(`/facturas/${factura.id}/quitar-firma`);
        notificaciones.success('Firma removida. El documento volvió a estado GENERADA.');
        await this.cargarFacturas();
      } catch (error) {
        notificaciones.error('No se pudo quitar la firma: ' + (error.response?.data?.error || error.message));
      }
    },

    async cargarEsAdministrador() {
      try {
        const resp = await controlAcceso.misPermisos();
        this.esAdministrador = !!resp?.data?.es_administrador;
      } catch (e) {
        console.warn('No se pudo obtener el rol del usuario.', e);
      }
    },

    async aprobarDescuento(factura) {
      this.procesandoDescuento = { ...this.procesandoDescuento, [factura.id]: true };
      try {
        await api.post(`/facturas/${factura.id}/aprobar-descuento`);
        notificaciones.success('Descuento aprobado. La factura ya puede firmarse y enviarse.');
        await this.cargarFacturas();
      } catch (error) {
        notificaciones.error('No se pudo aprobar el descuento: ' + (error.response?.data?.error || error.message));
      } finally {
        this.procesandoDescuento = { ...this.procesandoDescuento, [factura.id]: false };
      }
    },

    async rechazarDescuento(factura) {
      this.procesandoDescuento = { ...this.procesandoDescuento, [factura.id]: true };
      try {
        await api.post(`/facturas/${factura.id}/rechazar-descuento`);
        notificaciones.success('Descuento rechazado.');
        await this.cargarFacturas();
      } catch (error) {
        notificaciones.error('No se pudo rechazar el descuento: ' + (error.response?.data?.error || error.message));
      } finally {
        this.procesandoDescuento = { ...this.procesandoDescuento, [factura.id]: false };
      }
    },

    /**
     * Regenera el XML de una factura RECHAZADA o con ERROR_TECNICO
     * con una nueva clave de acceso (fecha de hoy), sin necesidad de editarla.
     */
    async regenerarXml(factura) {
      this.procesandoRegenerar = { ...this.procesandoRegenerar, [factura.id]: true };
      try {
        const { data } = await api.post(`/facturas/${factura.id}/regenerar-xml`);
        notificaciones.exito(data.message || 'XML regenerado correctamente.');
        await this.cargarFacturas();
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Error inesperado';
        notificaciones.error('Error al regenerar XML: ' + msg);
      } finally {
        this.procesandoRegenerar = { ...this.procesandoRegenerar, [factura.id]: false };
      }
    },

    /**
     * Consulta al SRI el estado de autorización de una factura en estado ENVIADA.
     * Útil cuando la autorización quedó pendiente tras el envío inicial.
     */
    async consultarAutorizacion(factura) {
      this.procesandoConsultaAuth = {
        ...this.procesandoConsultaAuth,
        [factura.id]: true
      };
      try {
        const { data } = await api.post(`/facturas/${factura.id}/consultar-autorizacion`);

        if (data.success) {
          // Limpiar estado sin_comprobantes si se autorizó
          this.facturasSinComprobantes[factura.id] = false;
          await this.cargarFacturas();
          const datosAutorizacion = this.mostrarModalAutorizacion(data, factura);
          notificaciones.success(
            `Factura ${factura.numero_factura || factura.id} AUTORIZADA por el SRI. Num: ${datosAutorizacion.numero_autorizacion || 'N/A'}`
          );
        } else if (data.en_proceso) {
          if (data.sin_comprobantes) {
            // El SRI no tiene esta clave en su sistema de autorización (0 comprobantes).
            // Puede ser un problema del ambiente de pruebas o un delay prolongado.
            this.facturasSinComprobantes[factura.id] = true;
            notificaciones.warning(
              data.message ||
              'El SRI todavía no registra el comprobante. El sistema conservará la firma y lo reenviará automáticamente cada 30 minutos.'
            );
          } else {
            notificaciones.warning(
              data.message ||
              'El SRI aún está procesando el comprobante. Consulte nuevamente en 1-2 minutos.'
            );
          }
          // Cooldown de 60 s para evitar spam al botón de consultar
          this.cooldownConsultaAuth[factura.id] = true;
          setTimeout(() => { this.cooldownConsultaAuth[factura.id] = false; }, 60000);
        } else {
          notificaciones.error('SRI rechazó el comprobante: ' + (data.message || data.error || 'Error desconocido'));
          this.facturasSinComprobantes[factura.id] = false;
          await this.cargarFacturas();
        }
      } catch (error) {
        const status = error.response?.status;
        const data   = error.response?.data;
        const msg    = data?.message || data?.error || error.message;

        if (status === 202) {
          if (data?.sin_comprobantes) {
            this.facturasSinComprobantes[factura.id] = true;
            notificaciones.warning(
              data.message ||
              'El SRI todavía no registra el comprobante. El sistema lo reenviará automáticamente cada 30 minutos.'
            );
          } else {
            notificaciones.warning(
              data?.message ||
              'El SRI aún está procesando el comprobante. Consulte nuevamente en 1-2 minutos.'
            );
          }
          this.cooldownConsultaAuth[factura.id] = true;
          setTimeout(() => { this.cooldownConsultaAuth[factura.id] = false; }, 60000);
        } else if (status === 422) {
          notificaciones.error('SRI rechazó el comprobante: ' + msg);
          this.facturasSinComprobantes[factura.id] = false;
          await this.cargarFacturas();
        } else {
          notificaciones.error('Error al consultar autorización: ' + msg);
        }
      } finally {
        this.procesandoConsultaAuth = {
          ...this.procesandoConsultaAuth,
          [factura.id]: false
        };
      }
    },

    // Reenviar / consultar autorización de una NOTA DE CRÉDITO en estado
    // FIRMADA, ENVIADA o RECHAZADA. El endpoint reutiliza el XML firmado,
    // reenvía al SRI y consulta la autorización en una sola llamada.
    async procesarNotaCreditoSri(factura) {
      if (this.procesandoConsultaAuth[factura.id]) return;
      this.procesandoConsultaAuth = { ...this.procesandoConsultaAuth, [factura.id]: true };
      try {
        notificaciones.info('Procesando nota de crédito en el SRI...');
        const respuesta = await api.post(`/notas-credito/${factura.id}/firmar`);

        if (respuesta.status === 202 || respuesta.data?.en_proceso) {
          notificaciones.warning(
            respuesta.data?.message ||
            'El SRI está procesando la nota de crédito. Consulte nuevamente en 1-2 minutos.'
          );
          this.cooldownConsultaAuth[factura.id] = true;
          setTimeout(() => { this.cooldownConsultaAuth[factura.id] = false; }, 60000);
          await this.cargarFacturas();
          return;
        }

        await this.cargarFacturas();
        notificaciones.success(
          `Nota de crédito autorizada por el SRI. Nº: ${respuesta.data?.numero_autorizacion || 'N/A'}`
        );
      } catch (error) {
        const status = error.response?.status;
        const datos  = error.response?.data;
        const codigo = datos?.codigo;
        const msg    = datos?.message || datos?.error || error.message;

        if (status === 400 && (codigo === 'CERT_SIN_CLAVE' || codigo === 'CERT_CLAVE_INVALIDA')) {
          notificaciones.error('El certificado digital necesita configurarse. Vaya a Configuración → Firma Electrónica.');
        } else if (status === 202) {
          notificaciones.warning(msg || 'El SRI está procesando la nota de crédito. Consulte en 1-2 minutos.');
          this.cooldownConsultaAuth[factura.id] = true;
          setTimeout(() => { this.cooldownConsultaAuth[factura.id] = false; }, 60000);
          await this.cargarFacturas();
        } else if (status === 422) {
          notificaciones.error('El SRI rechazó la nota de crédito: ' + msg);
          await this.cargarFacturas();
        } else {
          notificaciones.error('Error al procesar la nota de crédito: ' + msg);
        }
      } finally {
        this.procesandoConsultaAuth = { ...this.procesandoConsultaAuth, [factura.id]: false };
      }
    },

    async esperarAutorizacionAutomatica(factura, maxIntentos = 5, esperaMs = 12000) {
      if (!factura?.id) {
        return;
      }

      this.procesandoConsultaAuth = {
        ...this.procesandoConsultaAuth,
        [factura.id]: true
      };

      try {
        for (let intento = 1; intento <= maxIntentos; intento += 1) {
          await new Promise((resolve) => setTimeout(resolve, esperaMs));

          try {
            const { data } = await api.post(`/facturas/${factura.id}/consultar-autorizacion`);

            if (data?.success) {
              this.facturasSinComprobantes[factura.id] = false;
              await this.cargarFacturas();
              const datosAutorizacion = this.mostrarModalAutorizacion(data, factura);
              notificaciones.success(
                `Factura ${factura.numero_factura || factura.id} autorizada por el SRI. Num: ${datosAutorizacion.numero_autorizacion || 'N/A'}`
              );
              return;
            }

            // Clave rotada: la factura quedó en GENERADA, no tiene sentido seguir haciendo polling.
            if (data?.clave_rotada) {
              this.facturasSinComprobantes[factura.id] = true;
              notificaciones.warning(
                data.message || 'Se generó una nueva clave de acceso. Haga clic en Firmar y Enviar nuevamente.'
              );
              await this.cargarFacturas();
              return;
            }

            if (!data?.en_proceso) {
              notificaciones.error('SRI rechazó el comprobante: ' + (data?.message || data?.error || 'Error desconocido'));
              this.facturasSinComprobantes[factura.id] = false;
              await this.cargarFacturas();
              return;
            }

            // Detectar caso sin_comprobantes — dejar de reintentar, informar al usuario
            if (data?.sin_comprobantes) {
              this.facturasSinComprobantes[factura.id] = true;
            }
          } catch (error) {
            const status = error.response?.status;
            const data   = error.response?.data;
            const msg    = data?.message || data?.error || error.message;

            if (status === 202) {
              if (data?.sin_comprobantes) {
                this.facturasSinComprobantes[factura.id] = true;
              }
              continue;
            }

            if (status === 422) {
              notificaciones.error('SRI rechazó el comprobante: ' + msg);
              this.facturasSinComprobantes[factura.id] = false;
              await this.cargarFacturas();
              return;
            }

            notificaciones.error('Error al consultar autorización automática: ' + msg);
            return;
          }
        }

        if (this.facturasSinComprobantes[factura.id]) {
          notificaciones.warning(
            'El SRI todavía no registra el comprobante. La firma y la clave se conservaron; el sistema lo reenviará automáticamente cada 30 minutos.'
          );
        } else {
          notificaciones.warning('El SRI sigue procesando el comprobante. La factura queda en estado ENVIADA y estará disponible para consulta manual en 1-2 minutos.');
        }
        // Activar cooldown para que el usuario no intente de inmediato
        if (factura?.id) {
          this.cooldownConsultaAuth[factura.id] = true;
          setTimeout(() => { this.cooldownConsultaAuth[factura.id] = false; }, 60000);
        }
        await this.cargarFacturas();
      } finally {
        this.procesandoConsultaAuth = {
          ...this.procesandoConsultaAuth,
          [factura.id]: false
        };
      }
    },

    async anularFactura(factura) {
      try {
        const endpoint = factura.tipo_doc === 'nota_credito'
          ? `/notas-credito/${factura.id}/anular`
          : factura.tipo_doc === 'nota_venta'
            ? `/notas-venta/${factura.id}/anular`
            : `/facturas/${factura.id}/anular`;
        const { data } = await api.post(endpoint);

        if (data?.aviso_sri) {
          // Factura era AUTORIZADA: anulada en el sistema pero requiere gestión en SRI
          notificaciones.success('Factura marcada como ANULADA en el sistema.');
          // Mostrar aviso separado y persistente con instrucciones del SRI
          setTimeout(() => {
            notificaciones.warning(
              '⚠️ Acción requerida en el SRI: Para que la anulación sea legalmente válida, ' +
              'ingrése al portal sri.gob.ec → Servicios en Línea → Anulación de Comprobantes Electrónicos. ' +
              'Plazo: dentro del mes siguiente a la fecha de emisión de la factura.',
              { duracion: 12000 }
            );
          }, 500);
        } else {
          notificaciones.success('Documento anulado correctamente.');
        }

        await this.cargarFacturas();
      } catch (error) {
        notificaciones.error('No se pudo anular el documento: ' + (error.response?.data?.error || error.message));
      }
    },

    async eliminarFactura(factura) {
      try {
        const { data } = await api.delete(`/facturas/${factura.id}`);
        notificaciones.success(data?.message || 'Factura eliminada correctamente.');
        await this.cargarFacturas();
      } catch (error) {
        notificaciones.error('No se pudo eliminar la factura: ' + (error.response?.data?.error || error.message));
      }
    },

    // ── Helpers de tipo de documento ─────────────────────────────────────────

    etiquetaTipo(tipo) {
      const mapa = {
        'factura':       'Factura',
        'nota_credito':  'Nota Cred.',
        'guia_remision': 'Guía Rem.',
        'nota_venta':    'Nota Venta'
      };
      return mapa[tipo] || 'Factura';
    },

    claseTipo(tipo) {
      const mapa = {
        'factura':       'tipo-factura',
        'nota_credito':  'tipo-nota-credito',
        'guia_remision': 'tipo-guia-remision',
        'nota_venta':    'tipo-nota-venta'
      };
      return mapa[tipo] || 'tipo-factura';
    },

    // ── Helpers de estado ────────────────────────────────────────────────────

    puedeFirmarDocumento(factura) {
      // FIRMADA tiene su propio botón "Enviar al SRI"; aquí solo los que necesitan firmar + enviar
      return Number(factura.estado) === ESTADO.GENERADA;
    },

    puedeEnviarSri(factura) {
      const estado = Number(factura.estado);
      return estado === ESTADO.FIRMADA || estado === ESTADO.RECHAZADA;
    },

    puedeQuitarFirma(factura) {
      return Number(factura.estado) === ESTADO.FIRMADA;
    },

    puedeDescargarRide(factura) {
      return [ESTADO.FIRMADA, ESTADO.ENVIADA, ESTADO.AUTORIZADA, ESTADO.ANULADA]
        .includes(Number(factura.estado));
    },

    puedeRegenerarXml() {
      // Legado conservado: la regeneración ya no se ofrece en esta sección.
      return false;
    },

    puedeEditarFactura(factura) {
      const estado = Number(factura.estado);
      if ([ESTADO.BORRADOR, ESTADO.GENERADA].includes(estado)) {
        return true;
      }
      return estado === ESTADO.RECHAZADA && this.debeRegenerarXml(factura);
    },

    debeRegenerarXml(factura) {
      if (!factura?.fecha_emision) {
        return false;
      }
      return factura.fecha_emision < this.hoy();
    },

    puedeEliminar(factura) {
      const estado = Number(factura.estado);
      // FIRMADA se permite únicamente tras la verificación segura del backend contra el SRI.
      return [ESTADO.BORRADOR, ESTADO.GENERADA, ESTADO.FIRMADA].includes(estado);
    },

    puedeAnular(factura) {
      // Solo comprobantes legalmente autorizados por el SRI pueden anularse
      if (Number(factura.estado) !== ESTADO.AUTORIZADA) return false;
      // Desde el 01/01/2026 el SRI no permite anular facturas a consumidor final
      const esConsumidorFinal =
        factura.cliente_identificacion === '9999999999999' ||
        String(factura.cliente_tipo_identificacion || '').toUpperCase() === '07';
      if (esConsumidorFinal) {
        const fechaEmision = new Date(factura.fecha_emision || factura.created_at);
        if (fechaEmision >= new Date('2026-01-01')) return false;
      }
      return true;
    },

    claseEstado(estado) {
      const mapa = {
        [ESTADO.BORRADOR]:      'estado-borrador',
        [ESTADO.GENERADA]:      'estado-generada',
        [ESTADO.FIRMADA]:       'estado-firmada',
        [ESTADO.ENVIADA]:       'estado-enviada',
        [ESTADO.AUTORIZADA]:    'estado-autorizada',
        [ESTADO.RECHAZADA]:     'estado-rechazada',
        [ESTADO.ERROR_TECNICO]: 'estado-error',
        [ESTADO.ANULADA]:       'estado-anulada'
      };
      return mapa[Number(estado)] || 'estado-borrador';
    },

    textoEstado(estado) {
      const mapa = {
        [ESTADO.BORRADOR]:      'BORRADOR',
        [ESTADO.GENERADA]:      'GENERADA',
        [ESTADO.FIRMADA]:       'FIRMADA',
        [ESTADO.ENVIADA]:       'ENVIADA',
        [ESTADO.AUTORIZADA]:    'AUTORIZADA',
        [ESTADO.RECHAZADA]:     'RECHAZADA',
        [ESTADO.ERROR_TECNICO]: 'ERROR TÉCNICO',
        [ESTADO.ANULADA]:       'ANULADA'
      };
      return mapa[Number(estado)] || '-';
    },

    // ── Formateo ─────────────────────────────────────────────────────────────

    formatearFecha(fecha) {
      if (!fecha) return '-';
      try {
        return new Date(fecha + 'T00:00:00').toLocaleDateString('es-EC', {
          day: '2-digit', month: '2-digit', year: 'numeric'
        });
      } catch { return fecha; }
    },

    formatearFechaHora(fechaHora) {
      return formatearFechaHoraSistema(fechaHora);
    },

    formatearMonto(valor) {
      const numero = parseFloat(valor);
      return isNaN(numero) ? '0.00' : numero.toFixed(2);
    },

    primerDiaMes() {
      const hoy = new Date();
      return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
    },

    hoy() {
      const hoy = new Date();
      return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    },

    // ── Correo ───────────────────────────────────────────────────────────────

    abrirModalCorreo(factura) {
      this.modalCorreo = {
        visible:    true,
        factura,
        correo:     factura.cliente_email || '',
        procesando: false
      };
    },

    cerrarModalCorreo() {
      if (!this.modalCorreo.procesando) {
        this.modalCorreo.visible = false;
      }
    },

    async ejecutarReenvioCorreo() {
      if (this.modalCorreo.procesando) return;
      this.modalCorreo.procesando = true;
      try {
        const carga = {};
        const correoOverride = this.modalCorreo.correo.trim();
        if (correoOverride) {
          carga.correo = correoOverride;
        }
        const factura = this.modalCorreo.factura;
        const endpoint = factura.tipo_doc === 'nota_credito'
          ? `/notas-credito/${factura.id}/reenviar-correo`
          : `/facturas/${factura.id}/reenviar-correo`;
        const { data } = await api.post(endpoint, carga);
        notificaciones.success(data.message || 'Comprobante enviado por correo correctamente.');
        this.modalCorreo.visible = false;
      } catch (error) {
        const msg = error.response?.data?.error || error.message;
        notificaciones.error('No se pudo enviar el correo: ' + msg);
      } finally {
        this.modalCorreo.procesando = false;
      }
    }
  }
};
</script>

<style scoped>
/* ══════════════════════════════════════════════════════════════════════════════
   Variables del sistema (heredadas de SYSTEM.md §13)
   Se usan las variables globales: --primary-main, --bg-main, --secondary-bg-main,
   --text-primary, --text-secondary, --text-muted, --border-main, --border-focus,
   --accent-success, --accent-warning, --accent-danger, --shadow-sm, --shadow-md
   ══════════════════════════════════════════════════════════════════════════════ */

/* ── Layout general ──────────────────────────────────────────────────────────── */
.documentos-emitidos {
  padding: 1.5rem;
  color: var(--text-primary);
}

/* ── Encabezado de página ────────────────────────────────────────────────────── */
.encabezado-pagina {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.encabezado-pagina h1 {
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.encabezado-pagina h1 i {
  margin-right: 0.5rem;
  color: var(--primary-main);
}

.subtitulo {
  color: var(--text-secondary);
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
}

.encabezado-acciones {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* ── Botones del sistema ─────────────────────────────────────────────────────── */
.boton {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  text-decoration: none;
}

.boton:disabled { opacity: 0.55; cursor: not-allowed; }

.boton-primario  { background: var(--primary-main); color: #fff; border-color: var(--primary-main); }
.boton-primario:hover { background: var(--primary-hover); border-color: var(--primary-hover); }

.boton-secundario { background: transparent; color: var(--text-secondary); border-color: var(--border-main); }
.boton-secundario:hover { background: var(--bg-main); }

.boton-exito  { background: var(--accent-success); color: #fff; border-color: var(--accent-success); }
.boton-exito:hover { filter: brightness(0.9); }

.boton-peligro { background: var(--accent-danger); color: #fff; border-color: var(--accent-danger); }
.boton-peligro:hover { filter: brightness(0.9); }

.boton-contorno-primario  { background: transparent; color: var(--primary-main); border-color: var(--primary-main); }
.boton-contorno-primario:hover { background: var(--primary-main); color: #fff; }

.boton-contorno-secundario { background: transparent; color: var(--text-secondary); border-color: var(--border-main); }
.boton-contorno-secundario:hover { background: var(--bg-main); }

.boton-pequeno { padding: 0.3rem 0.7rem; font-size: 0.8rem; }

/* ── Filtros ─────────────────────────────────────────────────────────────────── */
.tarjeta-filtros {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
  box-shadow: var(--shadow-sm);
}

.fila-filtros {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-end;
}

.grupo-filtro {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 145px;
}

.grupo-filtro label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.grupo-filtro-acciones {
  min-width: auto;
  margin-top: auto;
}

/* ── Campos de entrada ───────────────────────────────────────────────────────── */
.campo-entrada {
  padding: 0.45rem 0.75rem;
  border: 1.5px solid var(--border-main);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  transition: border-color 0.15s;
  width: 100%;
}

.campo-entrada:focus {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-main) 15%, transparent);
}

/* ── Tabla ───────────────────────────────────────────────────────────────────── */
.tarjeta-tabla {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.tabla-responsiva { overflow-x: auto; }

.tabla { width: 100%; border-collapse: collapse; }

.tabla thead th {
  background: var(--bg-main);
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 2px solid var(--border-main);
  padding: 0.75rem 1rem;
  white-space: nowrap;
}

.tabla tbody td {
  padding: 0.7rem 1rem;
  vertical-align: middle;
  font-size: 0.875rem;
  border-bottom: 1px solid var(--border-main);
  color: var(--text-primary);
}

.tabla tbody tr:last-child td { border-bottom: none; }

.tabla tbody tr:hover { background: var(--bg-main); }

.numero-factura {
  color: var(--primary-main);
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px dashed var(--primary-main);
  transition: border-bottom-style 0.15s;
}
a.numero-factura:hover {
  border-bottom-style: solid;
}

.celda-cliente { display: flex; flex-direction: column; }
.nombre-cliente { font-weight: 600; }
.identificacion-cliente { font-size: 0.75rem; color: var(--text-muted); }

.text-end { text-align: right !important; }
.text-center { text-align: center !important; }

/* ── Etiquetas de estado ─────────────────────────────────────────────────────── */
.etiqueta-estado {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.estado-borrador      { background: var(--border-main);     color: var(--text-secondary); }
.estado-generada      { background: color-mix(in srgb, var(--primary-main) 15%, transparent); color: var(--primary-main); }
.estado-firmada       { background: color-mix(in srgb, var(--primary-main) 20%, transparent); color: var(--primary-hover); }
.estado-enviada       { background: color-mix(in srgb, var(--accent-warning) 20%, transparent); color: var(--accent-warning); }
.estado-autorizada    { background: color-mix(in srgb, var(--accent-success) 20%, transparent); color: var(--accent-success); }
.estado-rechazada     { background: color-mix(in srgb, var(--accent-danger) 20%, transparent); color: var(--accent-danger); }
.estado-error         { background: color-mix(in srgb, var(--accent-danger) 20%, transparent); color: var(--accent-danger); }
.estado-anulada       { background: var(--bg-main); color: var(--text-muted); }

.etiqueta-descuento-pendiente {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: color-mix(in srgb, var(--accent-warning) 20%, transparent);
  color: var(--accent-warning);
}

.aviso-sri-pendiente {
  display: block;
  margin-top: 0.35rem;
  color: var(--accent-warning);
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: help;
}

.informacion-sri {
  display: inline-block;
  position: relative;
  margin-left: 0.35rem;
  color: var(--primary-main);
  font-size: 0.75rem;
  cursor: help;
}

.detalle-informacion-sri {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  width: 230px;
  max-width: 70vw;
  padding: 0.6rem;
  white-space: normal;
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: 0.35rem;
  box-shadow: var(--shadow-md);
  font-weight: normal;
}

.informacion-sri:hover .detalle-informacion-sri,
.informacion-sri:focus .detalle-informacion-sri {
  display: block;
}

/* ── Badges de tipo de documento ─────────────────────────────────────────────── */
.etiqueta-tipo {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 0.3rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.tipo-factura       { background: color-mix(in srgb, var(--primary-main) 15%, transparent); color: var(--primary-main); }
.tipo-nota-credito  { background: color-mix(in srgb, var(--accent-warning) 15%, transparent); color: var(--accent-warning); }
.tipo-guia-remision { background: color-mix(in srgb, var(--accent-success) 15%, transparent); color: var(--accent-success); }
.tipo-nota-venta    { background: color-mix(in srgb, var(--text-muted) 20%, transparent); color: var(--text-secondary); }

/* ── Filtro cliente ──────────────────────────────────────────────────────────── */
.grupo-filtro-cliente {
  min-width: 220px;
  flex: 1 1 220px;
}

.texto-ayuda-accion {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Botones de acción por fila ──────────────────────────────────────────────── */
.grupo-acciones {
  display: flex;
  gap: 0.3rem;
  justify-content: center;
  flex-wrap: wrap;
}

.boton-accion {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 0.4rem;
  border: 1.5px solid transparent;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.boton-accion-exito     { background: color-mix(in srgb, var(--accent-success) 15%, transparent); color: var(--accent-success); border-color: color-mix(in srgb, var(--accent-success) 40%, transparent); }
.boton-accion-exito:hover { background: var(--accent-success); color: #fff; }

.boton-accion-info      { background: color-mix(in srgb, var(--primary-main) 15%, transparent); color: var(--primary-main); border-color: color-mix(in srgb, var(--primary-main) 40%, transparent); }
.boton-accion-info:hover { background: var(--primary-main); color: #fff; }

.boton-accion-peligro   { background: color-mix(in srgb, var(--accent-danger) 15%, transparent); color: var(--accent-danger); border-color: color-mix(in srgb, var(--accent-danger) 40%, transparent); }
.boton-accion-peligro:hover { background: var(--accent-danger); color: #fff; }

.boton-accion-peligro-contorno { background: transparent; color: var(--accent-danger); border-color: var(--accent-danger); }
.boton-accion-peligro-contorno:hover { background: var(--accent-danger); color: #fff; }

.boton-accion-info { background: color-mix(in srgb, var(--accent-info, #0ea5e9) 15%, transparent); color: var(--accent-info, #0ea5e9); border-color: color-mix(in srgb, var(--accent-info, #0ea5e9) 40%, transparent); }
.boton-accion-info:hover:not(:disabled) { background: var(--accent-info, #0ea5e9); color: #fff; }
.boton-accion-info:disabled { opacity: 0.5; cursor: not-allowed; }

.boton-accion-advertencia { background: color-mix(in srgb, var(--accent-warning) 15%, transparent); color: var(--accent-warning); border-color: color-mix(in srgb, var(--accent-warning) 40%, transparent); }
.boton-accion-advertencia:hover { background: var(--accent-warning); color: #fff; }

.boton-accion-neutro    { background: var(--bg-main); color: var(--text-secondary); border-color: var(--border-main); }
.boton-accion-neutro:hover { background: var(--border-main); }

/* ── Estados de carga y vacío ────────────────────────────────────────────────── */
.estado-carga,
.estado-vacio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  gap: 0.75rem;
  text-align: center;
}

.estado-vacio i { font-size: 3rem; }

.indicador-carga {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border-main);
  border-top-color: var(--primary-main);
  border-radius: 50%;
  animation: girar 0.8s linear infinite;
}

.indicador-carga-pequeno {
  width: 14px;
  height: 14px;
  border-width: 2px;
  display: inline-block;
}

@keyframes girar { to { transform: rotate(360deg); } }
@keyframes girando { to { transform: rotate(360deg); } }
.girando { animation: girando 0.8s linear infinite; display: inline-block; }

/* ── Paginación ──────────────────────────────────────────────────────────────── */
.paginacion {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid var(--border-main);
}

.info-pagina {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* ── Modales ─────────────────────────────────────────────────────────────────── */
.capa-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.caja-modal {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-md);
  width: 100%;
  max-width: 500px;
  overflow: hidden;
}

.caja-modal-amplia  { max-width: 620px; }
.caja-modal-compacta { max-width: 420px; }

.modal-encabezado {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: var(--bg-main);
  border-bottom: 1px solid var(--border-main);
}

.modal-encabezado h5 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.encabezado-exito { background: color-mix(in srgb, var(--accent-success) 12%, var(--secondary-bg-main)); }
.encabezado-exito h5 { color: var(--accent-success); }

.encabezado-error { background: color-mix(in srgb, var(--accent-danger) 12%, var(--secondary-bg-main)); }
.encabezado-error h5 { color: var(--accent-danger); }

.encabezado-advertencia { background: color-mix(in srgb, var(--accent-warning) 12%, var(--secondary-bg-main)); }
.encabezado-advertencia h5 { color: var(--accent-warning); }

.boton-cerrar-modal {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 1rem;
  padding: 0.2rem;
  border-radius: 0.35rem;
  transition: color 0.15s, background 0.15s;
}

.boton-cerrar-modal:hover { color: var(--text-primary); background: var(--border-main); }
.boton-cerrar-modal:disabled { opacity: 0.4; cursor: not-allowed; }

.modal-cuerpo {
  padding: 1.25rem;
  color: var(--text-primary);
}

.modal-cuerpo p { margin: 0 0 0.75rem; line-height: 1.6; }

.modal-pie {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border-main);
  flex-wrap: wrap;
}

/* ── Campos dentro del modal ─────────────────────────────────────────────────── */
.grupo-campo { display: flex; flex-direction: column; gap: 0.3rem; }

.etiqueta-campo {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.texto-ayuda {
  font-size: 0.77rem;
  color: var(--text-muted);
}

/* ── Info de autorización ────────────────────────────────────────────────────── */
.info-autorizacion { display: flex; flex-direction: column; gap: 0.85rem; }

.fila-dato { display: flex; flex-direction: column; gap: 0.1rem; }

.etiqueta-dato {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

.valor-dato {
  font-size: 0.92rem;
  color: var(--text-primary);
}

.valor-codigo {
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  word-break: break-all;
  background: var(--bg-main);
  padding: 0.2rem 0.4rem;
  border-radius: 0.3rem;
  border: 1px solid var(--border-main);
}

.valor-codigo-pequeno { font-size: 0.73rem; }

.texto-error { color: var(--accent-danger); font-weight: 600; }
</style>
